import React, { useState, useMemo, useEffect } from "react";
import { Modal, Table, Message, InputNumber } from "rsuite";
import { IconButton, Button, ChildSearchModal, Modal as CustomModal } from "../../../../components";
import TrashIcon from "@rsuite/icons/Trash";
import logger from "../../../../utils/logger";
import ToastService from "../../../../services/toast.service";
import TransactionsService from "./transactions.service";
import "./SplitTransactionModal.css";
import formatCurrency from '../../../../utils/formatCurrency';

const { Column, HeaderCell, Cell } = Table;

function SplitTransactionModal({ open, onClose, transaction, onConfirmSplit }) {
  const [childSplits, setChildSplits] = useState([]);
  const [childSearchModalOpen, setChildSearchModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loadingSplits, setLoadingSplits] = useState(false);
  const [deleteAllModalOpen, setDeleteAllModalOpen] = useState(false);

  // Parse transaction amount as number for calculations
  const transactionAmount = useMemo(() => {
    if (!transaction?.amount) return 0;
    const amount = Number(transaction.amount);
    return Number.isFinite(amount) ? amount : 0;
  }, [transaction?.amount]);

  // Calculate total allocated amount
  const totalAllocated = useMemo(() => {
    return childSplits.reduce((sum, split) => {
      const amount = Number(split.amount) || 0;
      return sum + amount;
    }, 0);
  }, [childSplits]);

  // Calculate remaining amount
  const remainingAmount = useMemo(() => {
    // Round to nearest cent to avoid floating point precision issues
    const raw = transactionAmount - totalAllocated;
    return Math.round(raw * 100) / 100;
    
  }, [transactionAmount, totalAllocated]);

  // Validation - check if amounts balance
  const isValid = useMemo(() => {
    return Math.abs(remainingAmount) < 0.01 && childSplits.length > 0;
  }, [remainingAmount, childSplits.length]);

  // Load existing splits when modal opens
  useEffect(() => {
    if (open && transaction?.id) {
      setLoadingSplits(true);
      setError("");
      TransactionsService.getTransactionSplits(transaction.id)
        .then((splits) => {
          const mapped = splits.map((split) => ({
            id: split.id,
            childId: split.childId,
            childName: split.childName,
            amount: split.amount,
          }));
          setChildSplits(mapped);
        })
        .catch((error) => {
          logger.error("Error loading splits:", error);
          setError("Failed to load existing splits");
        })
        .finally(() => {
          setLoadingSplits(false);
        });
    }
  }, [open, transaction?.id]);

  

  const handleChildSelect = (child) => {
    // Now we receive the full child object instead of a modified transaction
    const childId = child?.childId;
    const childName = child?.childFullName || "Unknown Child";

    if (!childId) {
      setError("No child ID selected");
      return;
    }

    // Check if child is already in the list
    if (childSplits.some((split) => split.childId === childId)) {
      setError(`Child ${childId} is already in the split list`);
      return;
    }

    // Add new child split with default amount
    const newSplit = {
      id: Date.now(), // Simple ID for React key
      childId: childId,
      childName: childName,
      amount: remainingAmount > 0 ? remainingAmount : 0,
    };

    setChildSplits((prev) => [...prev, newSplit]);
    setError("");
    setChildSearchModalOpen(false);
    // The ChildSearchModal will close itself and handle the transaction matching
    // We just need to add the child to our split list
    ToastService.success(`Child ${childId} added to split`);
  };

  const handleAmountChange = (splitId, newAmount) => {
    // Allow only numbers with at most 2 decimal places
    if (!/^\d*\.?\d{0,2}$/.test(newAmount)) return;
    const numAmount = Number(newAmount);
    if (isNaN(numAmount) || numAmount < 0) return; // Prevent negative or invalid amounts
    setChildSplits((prev) => prev.map((split) => (split.id === splitId ? { ...split, amount: newAmount } : split)));
    setError("");
  };

  const handleRemoveSplit = (splitId) => {
    setChildSplits((prev) => prev.filter((split) => split.id !== splitId));
    setError("");
  };

  const handleDeleteAll = async () => {
    setLoading(true);
    setError("");

    try {
      await TransactionsService.deleteTransactionSplits(transaction.id);
      setChildSplits([]);
      setDeleteAllModalOpen(false);
      ToastService.success("All transaction splits deleted successfully");
    } catch (error) {
      logger.error("Error deleting transaction splits:", error);
      setError("Failed to delete transaction splits. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!isValid) {
      setError("The total split amounts must equal the transaction amount");
      return;
    }

    if (childSplits.length === 0) {
      setError("Please add at least one child to split the transaction");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Prepare data for API
      const splitData = {
        transactionId: transaction.id,
        splits: childSplits.map((split) => ({
          childId: split.childId,
          amount: Number(split.amount),
        })),
      };

      await onConfirmSplit(splitData);

      ToastService.success("Transaction split successfully");
      handleClose();
    } catch (error) {
      logger.error("Error splitting transaction:", error);
      setError("Failed to split transaction. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setChildSplits([]);
    setError("");
    setLoading(false);
    setLoadingSplits(false);
    setDeleteAllModalOpen(false);
    onClose();
  };

  const handleAddChild = () => {
    setChildSearchModalOpen(true);
  };

  if (!transaction) return null;
  return (
    <>
      <Modal backdrop="static" keyboard={true} open={open} onClose={handleClose} size="lg" className="split-transaction-modal">
        <Modal.Header>
          <Modal.Title>Split Transaction</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="split-modal-content">
            {/* Transaction Summary */}
            <div className="transaction-summary">
              <h6>Transaction Details</h6>
              <div className="transaction-info-split">
                <div className="transaction-info-item">
                  <span className="label">Particulars:</span>
                  <span className="value">{transaction.particulars || "N/A"}</span>
                </div>
                <div className="transaction-info-item">
                  <span className="label">Code:</span>
                  <span className="value">{transaction.code || "N/A"}</span>
                </div>
                <div className="transaction-info-item">
                  <span className="label">Reference:</span>
                  <span className="value">{transaction.reference || "N/A"}</span>
                </div>
              </div>
            </div>

            {/* Split Summary */}
            <div className="split-summary">
              <div className="split-summary-row">
                <span>Transaction Amount:</span>
                <span>{formatCurrency(transactionAmount)}</span>
              </div>
              <div className="split-summary-row">
                <span>Total Allocated:</span>
                <span>{formatCurrency(totalAllocated)}</span>
              </div>
              <div className={`split-summary-row ${remainingAmount === 0 ? "balanced" : remainingAmount > 0 ? "remaining" : "over"}`}>
                <span>Remaining:</span>
                <span>{formatCurrency(remainingAmount)}</span>
              </div>
            </div>

            {/* Control Section */}
            <div className="control-section">
              <Button text="Add Child" appearance="primary" onClick={handleAddChild} disabled={loading || loadingSplits || deleteAllModalOpen} />
              <Button text="Delete All" appearance="subtle" onClick={() => setDeleteAllModalOpen(true)} disabled={loading || loadingSplits || childSplits.length === 0 || deleteAllModalOpen} color="red" />
            </div>

            {/* Error Message */}
            {error && (
              <Message showIcon type="error" className="error-message">
                {error}
              </Message>
            )}

            {/* Split Table */}
            <div className="split-table-container">
              {loadingSplits ? (
                <div className="loading-splits">
                  <p>Loading existing splits...</p>
                </div>
              ) : (
                <Table
                  width={927}
                  data={childSplits}
                  height={300}
                  bordered
                  cellBordered
                  className="split-table"
                  renderEmpty={() => (
                    <div className="empty-splits">
                      <p>No children added yet. Click "Add Child" to start splitting the transaction.</p>
                    </div>
                  )}
                >
                  <Column width={120}>
                    <HeaderCell>Child ID</HeaderCell>
                    <Cell dataKey="childId" />
                  </Column>

                  <Column width={500}>
                    <HeaderCell>Child Name</HeaderCell>
                    <Cell dataKey="childName" />
                  </Column>

                  <Column width={200}>
                    <HeaderCell>Amount</HeaderCell>
                    <Cell>
                      {(rowData) => (
                        <InputNumber
                          step={0.01}
                          precision={2}
                          formatter={(value) => formatCurrency(value)}
                          value={rowData.amount}
                          onChange={(value) => handleAmountChange(rowData.id, value)}
                          disabled={loading || loadingSplits}
                          style={{ width: "100%" }}
                        />
                      )}
                    </Cell>
                  </Column>

                  <Column width={107}>
                    <HeaderCell>Remove</HeaderCell>
                    <Cell>
                      {(rowData) => (
                        <IconButton
                          icon={<TrashIcon />}
                          appearance="subtle"
                          onClick={() => handleRemoveSplit(rowData.id)}
                          disabled={loading || loadingSplits || deleteAllModalOpen}
                          title="Remove Child"
                          width={30}
                          height={30}
                          className="remove-split-button"
                        />
                      )}
                    </Cell>
                  </Column>
                </Table>
              )}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button text="Split Transaction" appearance="primary" onClick={handleConfirm} disabled={!isValid || loading || loadingSplits || deleteAllModalOpen} loading={loading} width={140} />
          <Button text="Cancel" appearance="subtle" onClick={handleClose} disabled={loading || loadingSplits || deleteAllModalOpen} color="red" />
        </Modal.Footer>
      </Modal>

      {/* Child Search Modal */}
      <ChildSearchModal
        open={childSearchModalOpen}
        onClose={() => setChildSearchModalOpen(false)}
        onChildSelect={handleChildSelect}
        transaction={transaction}
      />

      {/* Delete All Confirmation Modal */}
      <CustomModal
        title="Delete All Splits"
        text="Are you sure you want to delete all transaction splits? This action cannot be undone."
        confirmButtonText="Delete All"
        cancelButtonText="Cancel"
        onConfirm={handleDeleteAll}
        onClose={() => setDeleteAllModalOpen(false)}
        open={deleteAllModalOpen}
        confirmText="DELETE ALL"
        loading={loading}
      />
    </>
  );
}

export default SplitTransactionModal;
